import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { ChatRoomErrorBoundary } from '~/components/ErrorBoundary';
import { Toast } from '~/components/atomic';
import { FullScreenLoading } from '~/components/common';
import { useCommonChatState, useGetChatList, useWebSocket } from '~/hooks';
import { MessageResponse } from '~/types';
import { useUserStore, useCommonChatStore } from '~/store';
import ChatHistoryModal from '../../components/common/BottomSheet/ChatHistory/ChatHistoryBottomSheet';
import Background from './Background';
import Chat from './Chat/Chat';

export interface ChatLayoutProps {
  isChatHistoryOpen: boolean;
  setIsChatHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
export default function ConceptLayout({
  isChatHistoryOpen,
  setIsChatHistoryOpen,
}: ChatLayoutProps) {
  const { id: chatroomId } = useParams();
  const email = useUserStore((state) => state.email);

  const { isWaitingChatRoomInfo, setIsWaitingChatRoomInfo } =
    useCommonChatStore((state) => ({
      isWaitingChatRoomInfo: state.isWaitingChatRoomInfo,
      setIsWaitingChatRoomInfo: state.setIsWaitingChatRoomInfo,
    }));

  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [socketConnectSuccess, setSocketConnectSuccess] = useState(false);

  const { webSocket, socketClose, waitingChat, setWaitingChat } = useWebSocket({
    chatRoomId: chatroomId,
    chatType: 'personal',
    fetchDone: true,
    email,
    setMessages,
    socketConnectSuccess,
    setSocketConnectSuccess,
  });

  const { avatarInfo, isLeaveRoomPending, handleSetWaitingChatTrue } =
    useCommonChatState({
      chatType: 'personal',
      chatRoomId: chatroomId,
      email,
      setWaitingChat,
    });

  const { isChatRoomError, title } = useGetChatList({
    chatroomId,
    email,
    socketConnectSuccess,
    setMessages,
    chatType: 'personal',
    successEnter: true,
  });

  useEffect(() => {
    if (socketConnectSuccess) return;
    setIsWaitingChatRoomInfo(true);
  }, [socketConnectSuccess]);

  if (isChatRoomError) {
    return <ChatRoomErrorBoundary />;
  }

  return (
    <>
      {isLeaveRoomPending && <FullScreenLoading />}
      {socketClose && <Toast type='error' text='socket close' />}

      <Container>
        {title && messages.length > 0 && (
          <Chat
            webSocket={webSocket}
            messages={messages}
            setMessages={setMessages}
            waitingChat={waitingChat}
            handleSetWaitingChatTrue={handleSetWaitingChatTrue}
          />
        )}
        <Background avatarInfo={avatarInfo} bgTextureUrl='/park.jpg' />
      </Container>
      <ChatHistoryModal
        messages={messages}
        onClose={() => setIsChatHistoryOpen(false)}
        isOpen={isChatHistoryOpen}
      />
    </>
  );
}

export const Container = styled.main`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
